import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import * as signalR from '@aspnet/signalr';
import { store } from 'react-notifications-component';
import '../tools/css/VideoLlamadaZego.css';
import $ from 'jquery'; // <-to import jquery
import 'bootstrap';

const ApiURL = process.env.REACT_APP_API_URL;

// URL del servidor de señalización de ZEGOCLOUD para este proyecto (Web).
// Se obtiene desde la consola de ZEGOCLOUD (Project > Server URL / WebSocket
// Server) — no es un dato que se pueda inventar, así que hay que confirmarlo
// ahí y reemplazar este valor.
const ZEGO_SERVER_URL = "wss://webliveroom-api.zegocloud.com/ws";

// Instancia de axios aparte para Consultations/Create: la instancia por
// defecto tiene un interceptor global (en Navbar.js) que le inyecta el
// header Authorization con el token de sesión de Medicall24 a TODAS las
// peticiones — si se usara esa misma instancia acá, ese interceptor se
// mezclaría con el Bearer de token_profesional que necesita este endpoint.
const axiosProfesional = axios.create();

// Instancia de axios aparte para el Graph API de WhatsApp/Meta: no debe
// llevar el interceptor de Navbar.js ni el Bearer de token_profesional, solo
// el token de Meta (mismo patrón ya usado en Citas.js).
const axiosMeta = axios.create();

function notifAtencion(mensaje){
  return {
    title:'Atención',
    message: mensaje,
    type:'danger',
    container:'top-right',
    animationIn:["animated", "fadeIn"],
    animationOut:["animated", "fadeOut"],
    dismiss:{ duration:10000, showIcon:true }
  };
}

function notifExito(mensaje){
  return {
    title:'Muy bien',
    message: mensaje,
    type:'success',
    container:'top-right',
    animationIn:["animated", "fadeIn"],
    animationOut:["animated", "fadeOut"],
    dismiss:{ duration:8000, showIcon:true }
  };
}

function capitalizarPalabras(texto){
  return texto ? texto.trim().toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase()))) : '';
}

// Edad legible a partir de la fecha de nacimiento: en años si ya cumplió al
// menos 1, en meses (+ días si aplica) si es bebé, y solo en días si aún no
// cumple 1 mes.
function calcularEdadTexto(birthday){
  if(!birthday) return '';
  const nacimiento = moment(birthday);
  const ahora = moment();
  const anios = ahora.diff(nacimiento, 'years');
  if(anios >= 1){
    return anios + (anios===1 ? ' año' : ' años');
  }
  const meses = ahora.diff(nacimiento, 'months');
  if(meses >= 1){
    const dias = ahora.diff(nacimiento.clone().add(meses, 'months'), 'days');
    return meses + (meses===1 ? ' mes' : ' meses') + (dias>0 ? (' ' + dias + (dias===1 ? ' día' : ' días')) : '');
  }
  const dias = ahora.diff(nacimiento, 'days');
  return dias + (dias===1 ? ' día' : ' días');
}

/**
 * Videollamada con ZEGOCLOUD (ZegoExpressEngine, SDK web) para que el
 * profesional (navegador) se encuentre con el paciente (celular, ya
 * conectado por su lado con la app móvil) en la misma sala.
 *
 * Uso: se monta una sola vez (con ref) en la página que lista las citas, y
 * se llama this.videoLlamadaRef.current.iniciarLlamada(cita) al hacer clic
 * en el ícono de video llamada de una cita de telemedicina confirmada.
 */
class VideoLlamadaZego extends Component {

  videoLocalRef = React.createRef();
  videoRemotoRef = React.createRef();

  zg = null;
  streamLocal = null;
  streamIdLocal = '';
  roomID = '';
  consultationIdParaFinalizar = null; // consultations.id de la cita (CargarAgendas) — para Call/End
  consultationIdCreado = null; // consultationId de la respuesta de Consultations/Create — para Call/Cancel

  signalRConnection = null;

  state = {
    activa: false,
    conectando: false,
    error: '',
    micActivo: true,
    camaraActiva: true,
    hayRemoto: false,
    nombreRemoto: '',
    nombrePaciente: '',
    avatarPaciente: '',
    avatarError: false,
    pacienteInfo: null,
    loading_button_whatsapp_llamada: true,
    active_button_whatsapp_llamada: true,
    formWhatsappLlamada: {
      nombre: '',
      hora: '',
      celular: '',
      institucion: ''
    }
  };

  componentDidMount(){
    this.conectarSignalR();
  }

  componentWillUnmount(){
    this.finalizarLlamada();
    this.desconectarSignalR();
  }

  // Conexión al NotificationHub para recibir en tiempo real lo que hace el
  // paciente del otro lado de la llamada (rechazar, colgar, no contestar,
  // ocupado, contestar) — así el panel no depende de sondear la API.
  conectarSignalR = () => {
    const protocol = new signalR.JsonHubProtocol();

    this.signalRConnection = new signalR.HubConnectionBuilder()
      .withUrl(ApiURL + '/api/notifications', {
        accessTokenFactory: () => sessionStorage.getItem('token_profesional')
      })
      .withHubProtocol(protocol)
      .build();

    this.signalRConnection.on('CallSignal', this.onCallSignal);

    this.signalRConnection.onclose(() => {
      console.log('[MEDICALL CallSignal] SignalR desconectado, reintentando en 3s...');
      setTimeout(() => this.iniciarConexionSignalR(), 3000);
    });

    this.iniciarConexionSignalR();
  };

  iniciarConexionSignalR = () => {
    if(!this.signalRConnection) return;

    this.signalRConnection.start()
    .then(() => {
      console.log('[MEDICALL CallSignal] Conexión SignalR iniciada');
      // Si había una llamada activa (ej. se perdió la conexión un momento),
      // se reconcilia el estado por si se perdió algún evento.
      this.verificarEstadoLlamada();
    })
    .catch((err) => console.log('[MEDICALL CallSignal] Error de conexión SignalR', err));
  };

  desconectarSignalR = () => {
    if(this.signalRConnection){
      try{
        this.signalRConnection.stop();
      }catch(error){
        console.log(error);
      }
      this.signalRConnection = null;
    }
  };

  // Respaldo del punto 5.5: si se perdió el evento de SignalR (reconexión,
  // pestaña reanudada), se reconcilia consultando el mismo CallState.
  verificarEstadoLlamada = () => {
    if(!this.consultationIdCreado) return;

    const tokenProfesional = sessionStorage.getItem('token_profesional');
    axiosProfesional.get(ApiURL + "/api/Consultations/" + this.consultationIdCreado + "/Call/State", { headers: { "Authorization": "Bearer " + tokenProfesional } })
    .then((response) => this.onCallSignal(response.data))
    .catch((error) => console.log(error));
  };

  // Handler único de eventos de la videollamada (CallSignal por SignalR, o
  // el mismo CallState por el respaldo GET .../Call/State). Acepta
  // camelCase o PascalCase, como indica el contrato v2.
  onCallSignal = (payload) => {
    if(!payload) return;

    console.log('[MEDICALL CallSignal]', payload);

    const signal = payload.signal ?? payload.Signal;
    const by = payload.by ?? payload.By;
    const reason = payload.reason ?? payload.Reason ?? 'La llamada finalizó';
    const consultationId = payload.consultationId ?? payload.ConsultationId;

    // Solo reaccionar si la señal es de la llamada que está activa en este
    // momento en este navegador (ignora ruido de otras consultas).
    if(!this.consultationIdCreado || String(consultationId)!==String(this.consultationIdCreado)) return;

    if(['rejected','cancelled','ended','timedout','busy'].includes(signal)){

      // El otro lado (paciente) ya notificó al backend por su cuenta — acá
      // solo se limpia la UI/Zego local, sin volver a llamar Call/End ni
      // Call/Cancel (eso ya lo hizo el paciente con su propia petición).
      this.finalizarLlamada();
      this.consultationIdParaFinalizar = null;
      this.consultationIdCreado = null;

      if(by==='patient'){
        store.addNotification(notifAtencion(reason));
      }

      this.setState({ activa:false, conectando:false, error:'', micActivo:true, camaraActiva:true, hayRemoto:false, nombreRemoto:'', nombrePaciente:'', avatarPaciente:'', avatarError:false, pacienteInfo:null, formWhatsappLlamada:{ nombre:'', hora:'', celular:'', institucion:'' } });

      this.props.onLlamadaFinalizada && this.props.onLlamadaFinalizada();

    }else if(signal==='accepted'){
      console.log('[MEDICALL CallSignal] El paciente contestó');
      this.setState({ conectando:false });
    }
  };

  iniciarLlamada = async (cita) => {
    if(!cita){
      return;
    }

    // cita.consultations es un arreglo (a veces vacío []); se toma el id de
    // la consulta más reciente (la última del arreglo).
    this.consultationIdParaFinalizar = (cita.consultations && cita.consultations.length) ? cita.consultations[cita.consultations.length - 1].id : null;

    const nombrePaciente = cita.patient && cita.patient.user
      ? [cita.patient.user.name1, cita.patient.user.name2, cita.patient.user.lastname1, cita.patient.user.lastname2].filter(Boolean).map(capitalizarPalabras).join(' ')
      : '';

    const pacienteInfo = (cita.patient && cita.patient.user) ? cita.patient.user : null;
    const avatarPaciente = (cita.patient && cita.patient.user && cita.patient.user.avatar) ? cita.patient.user.avatar : '';

    this.setState({ activa:true, conectando:true, error:'', nombrePaciente, pacienteInfo, avatarPaciente, avatarError:false, hayRemoto:false, nombreRemoto:'', formWhatsappLlamada:{ nombre:'', hora:'', celular:'', institucion:'' } });

    try{

      const tokenProfesional = sessionStorage.getItem('token_profesional');

      const response = await axiosProfesional.post(ApiURL + "/api/Consultations/Create", {
        patientId: cita.patientId,
        professionalId: cita.professionalId,
        accessRequestId: 0,
        citaId: cita.id,
        consultationId: 0,
        specialtyId: cita.specialtyId
      }, {
        headers: { "Authorization": "Bearer " + tokenProfesional }
      });

      console.log(response);
      const datos = response.data || {};
      const { token, roomName, appId, identity, userName } = datos;

      this.consultationIdCreado = datos.consultationId;
      this.roomID = roomName;
      this.zg = new ZegoExpressEngine(appId, ZEGO_SERVER_URL);

      this.zg.on('roomStreamUpdate', async (roomID, updateType, streamList) => {
        if(updateType==='ADD'){
          for(const streamInfo of streamList){
            try{
              const streamRemoto = await this.zg.startPlayingStream(streamInfo.streamID);
              if(this.videoRemotoRef.current){
                this.videoRemotoRef.current.srcObject = streamRemoto;
              }
              this.setState({ hayRemoto:true, nombreRemoto: (streamInfo.user && streamInfo.user.userName) || 'Paciente' });
            }catch(errorReproduccion){
              console.log(errorReproduccion);
            }
          }
        }else if(updateType==='DELETE'){
          streamList.forEach((streamInfo) => this.zg.stopPlayingStream(streamInfo.streamID));
          this.setState({ hayRemoto:false, nombreRemoto:'' });
        }
      });

      this.zg.on('roomStateUpdate', (roomID, state, errorCode) => {
        console.log('roomStateUpdate', roomID, state, errorCode);
        if(state==='DISCONNECTED' && errorCode){
          store.addNotification(notifAtencion("Se perdió la conexión con la videollamada."));
        }
      });

      await this.zg.loginRoom(roomName, token, { userID: identity, userName: userName }, { userUpdate:true });

      this.streamLocal = await this.zg.createStream({ camera: { audio:true, video:true } });
      if(this.videoLocalRef.current){
        this.videoLocalRef.current.srcObject = this.streamLocal;
      }

      this.streamIdLocal = identity + '_' + Date.now();
      this.zg.startPublishingStream(this.streamIdLocal, this.streamLocal);

      this.setState({ conectando:false });

    }catch(error){
      console.log(error);
      const mensaje = (error.response && error.response.data && error.response.data.message) || "No se pudo iniciar la videollamada";
      store.addNotification(notifAtencion(mensaje));

      // Si el error es de validación/negocio (400-499), se ofrece avisarle
      // al paciente por WhatsApp que el médico intentó comunicarse, con los
      // mismos datos que ya se tenían de la cita.
      const esError4xx = error.response && error.response.status>=400 && error.response.status<500;
      if(esError4xx && cita.patient && cita.patient.user){
        const nombrePacienteWs = [cita.patient.user.name1, cita.patient.user.name2, cita.patient.user.lastname1, cita.patient.user.lastname2].filter(Boolean).join(' ');
        this.setState({
          formWhatsappLlamada: {
            nombre: nombrePacienteWs,
            hora: moment(cita.fecha).format('hh:mm A'),
            celular: cita.patient.user.phone || '',
            institucion: (sessionStorage.getItem('institution') || '').trim()
          }
        });
      }

      this.setState({ conectando:false, error: mensaje });
    }
  };

alternarMicrofono = () => {
    if(!this.zg || !this.streamLocal) return;
    const nuevoValor = !this.state.micActivo;
    this.zg.mutePublishStreamAudio(this.streamLocal, !nuevoValor);
    this.setState({ micActivo: nuevoValor });
  };

  alternarCamara = () => {
    if(!this.zg || !this.streamLocal) return;
    const nuevoValor = !this.state.camaraActiva;
    this.zg.mutePublishStreamVideo(this.streamLocal, !nuevoValor);
    this.setState({ camaraActiva: nuevoValor });
  };

  finalizarLlamada = () => {
    if(this.zg){
      try{
        if(this.streamIdLocal) this.zg.stopPublishingStream(this.streamIdLocal);
        if(this.streamLocal) this.zg.destroyStream(this.streamLocal);
        if(this.roomID) this.zg.logoutRoom(this.roomID);
      }catch(error){
        console.log(error);
      }
    }
    this.zg = null;
    this.streamLocal = null;
    this.streamIdLocal = '';
    this.roomID = '';
  };

  // Avisa al backend que la consulta (videollamada) terminó o se canceló.
  // Se dispara sin bloquear el colgado en pantalla, que debe sentirse
  // inmediato:
  //  - Si el paciente SÍ llegó a entrar a la sala (hayRemoto), es un fin de
  //    llamada normal -> Call/End con consultations.id de la cita.
  //  - Si el médico cuelga estando solo (el paciente nunca entró) -> es una
  //    cancelación -> Call/Cancel con el consultationId que devolvió
  //    Consultations/Create.
  avisarFinDeLlamada = () => {
    const tokenProfesional = sessionStorage.getItem('token_profesional');
    const headers = { "Authorization": "Bearer " + tokenProfesional };

    if(this.state.hayRemoto){
      if(!this.consultationIdParaFinalizar) return;
      axiosProfesional.post(ApiURL + "/api/Consultations/" + this.consultationIdParaFinalizar + "/Call/End", {}, { headers })
      .then((response) => { console.log(response); this.props.onLlamadaFinalizada && this.props.onLlamadaFinalizada(); })
      .catch((error) => { console.log(error); this.props.onLlamadaFinalizada && this.props.onLlamadaFinalizada(); });
    }else{
      if(!this.consultationIdCreado) return;
      axiosProfesional.post(ApiURL + "/api/Consultations/" + this.consultationIdCreado + "/Call/Cancel", {}, { headers })
      .then((response) => { console.log(response); this.props.onLlamadaFinalizada && this.props.onLlamadaFinalizada(); })
      .catch((error) => { console.log(error); this.props.onLlamadaFinalizada && this.props.onLlamadaFinalizada(); });
    }

    this.consultationIdParaFinalizar = null;
    this.consultationIdCreado = null;
  };

  colgarLlamada = () => {
    this.avisarFinDeLlamada();
    this.finalizarLlamada();
    this.setState({ activa:false, conectando:false, error:'', micActivo:true, camaraActiva:true, hayRemoto:false, nombreRemoto:'', nombrePaciente:'', avatarPaciente:'', avatarError:false, pacienteInfo:null, formWhatsappLlamada:{ nombre:'', hora:'', celular:'', institucion:'' } });
  };

  nombreCompletoPaciente = () => {
    const p = this.state.pacienteInfo;
    if(!p) return '';
    return [p.name1, p.name2, p.lastname1, p.lastname2].filter(Boolean).map(capitalizarPalabras).join(' ');
  };

  urlAvatarPaciente = () => {
    return process.env.REACT_APP_API_URL + "/api/Files?path=" + this.state.avatarPaciente;
  };

  iconoAvatarPorDefecto = () => {
    const genero = ((this.state.pacienteInfo && this.state.pacienteInfo.gender) || '').trim().toLowerCase();
    if(genero.startsWith('f')) return 'fa-female';
    if(genero.startsWith('m')) return 'fa-male';
    return 'fa-user';
  };

  handleChangeWhatsappLlamada = (e) => {
    this.setState({
      formWhatsappLlamada: {
        ...this.state.formWhatsappLlamada,
        celular: e.target.value
      }
    });
  };

  /**
   * Notifica por WhatsApp (plantilla "notificar_llamada_telemedicina") que
   * el médico está intentando comunicarse, cuando Consultations/Create falló
   * (por eso no hay sala ni token de ZEGO todavía en este flujo).
   */
  EnviarWhatsappLlamada = async () => {
    const soloDigitos = this.state.formWhatsappLlamada.celular.replace(/\D/g, '');

    if(soloDigitos.length===0){
      store.addNotification(notifAtencion("Ingresa el número de celular del paciente"));
      return;
    }

    this.setState({ loading_button_whatsapp_llamada:false, active_button_whatsapp_llamada:false });

    try{

      await axiosMeta.post(
        "https://graph.facebook.com/v25.0/636239482898096/messages",
        {
          messaging_product: "whatsapp",
          to: "57" + soloDigitos,
          type: "template",
          template: {
            language: { code: "es", policy: "deterministic" },
            name: "notificar_llamada_telemedicina",
            components: [{
              type: "body",
              parameters: [
                { type: "text", text: this.state.formWhatsappLlamada.nombre },
                { type: "text", text: this.state.formWhatsappLlamada.hora },
                { type: "text", text: this.state.formWhatsappLlamada.institucion }
              ]
            }]
          }
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer EAAZAhYAaHHqUBSV6JDWHMYuiJ9vhNsAgEkQfI6XDvVrG4pCMKuRZBeE0aXiGXXHpZBqPl2hHfgf9qgT5vSIJ4ZBeZAzt34R8HJxlelHrOL95qIcegLRgtseqxdBq7vJ1GoqnVmsbZAEM5EZCHXutFh10eE1Wxr6dwMEyrXh3H261IDm7ZAa68hDtwWLgHxbWdQZDZD"
          }
        }
      );

      store.addNotification(notifExito("Notificación enviada por WhatsApp"));
      this.setState({ loading_button_whatsapp_llamada:true, active_button_whatsapp_llamada:true });
      $('#modal-whatsapp-llamada').modal('hide');

    }catch(error){
      console.log(error.response?.data || error.message);
      store.addNotification(notifAtencion("No se pudo enviar la notificación por WhatsApp"));
      this.setState({ loading_button_whatsapp_llamada:true, active_button_whatsapp_llamada:true });
    }
  };

  render(){
    if(!this.state.activa) return null;

    return (
      <div className="videollamada-zego">

        <div className="videollamada-zego__remoto-wrap">
          <video ref={this.videoRemotoRef} className="videollamada-zego__remoto" autoPlay playsInline></video>

          {!this.state.hayRemoto ? (
            <div className={"videollamada-zego__espera" + (this.state.error ? ' videollamada-zego__espera--error' : '')}>
              {this.state.error ? (
                <React.Fragment>
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{this.state.error}</span>
                  <span className="videollamada-zego__espera-ayuda">Cuelga e intenta más tarde.</span>
                  {this.state.formWhatsappLlamada.nombre ? (
                    <button type="button" className="videollamada-zego__btn-whatsapp" onClick={() => $('#modal-whatsapp-llamada').modal('show')}>
                      <i className="fab fa-whatsapp"></i> Avisarle al paciente por WhatsApp
                    </button>
                  ) : null}
                </React.Fragment>
              ) : this.state.conectando ? (
                <React.Fragment>
                  <i className="fas fa-circle-notch fa-spin"></i>
                  <span>Conectando la videollamada...</span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <i className="fas fa-user-clock"></i>
                  <span>Esperando a que {this.state.nombrePaciente || 'el paciente'} se conecte...</span>
                </React.Fragment>
              )}
            </div>
          ) : null}

          <div className="videollamada-zego__topbar">
            <i className="fas fa-video"></i>
            <span>{this.state.hayRemoto ? this.state.nombreRemoto : (this.state.nombrePaciente || 'Videollamada')}</span>
          </div>

          <video ref={this.videoLocalRef} className="videollamada-zego__local" autoPlay playsInline muted></video>
        </div>

        <div className="videollamada-zego__controles">
          <button type="button" className="videollamada-zego__btn" title="Información del paciente" disabled={!this.state.pacienteInfo} onClick={() => $('#modal-info-paciente').modal('show')}>
            <i className="fas fa-id-card"></i>
          </button>

          <button type="button" className={"videollamada-zego__btn" + (this.state.micActivo ? '' : ' videollamada-zego__btn--apagado')} title={this.state.micActivo ? 'Silenciar micrófono' : 'Activar micrófono'} onClick={this.alternarMicrofono}>
            <i className={"fas " + (this.state.micActivo ? 'fa-microphone' : 'fa-microphone-slash')}></i>
          </button>

          <button type="button" className="videollamada-zego__btn videollamada-zego__btn--colgar" title="Colgar" onClick={this.colgarLlamada}>
            <i className="fas fa-phone-slash"></i>
          </button>

          <button type="button" className={"videollamada-zego__btn" + (this.state.camaraActiva ? '' : ' videollamada-zego__btn--apagado')} title={this.state.camaraActiva ? 'Apagar cámara' : 'Activar cámara'} onClick={this.alternarCamara}>
            <i className={"fas " + (this.state.camaraActiva ? 'fa-video' : 'fa-video-slash')}></i>
          </button>
        </div>

        <div className="modal fade" id="modal-whatsapp-llamada" data-backdrop="static">
          <div className="modal-dialog modal-md">
            <div className="modal-content citas-respuesta-modal">
              <div className="modal-header citas-respuesta-modal__header">
                <h4 className="modal-title citas-respuesta-modal__title"><i className="fab fa-whatsapp"></i> Avisar al paciente por WhatsApp</h4>
                <button type="button" className="close citas-respuesta-modal__close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body citas-respuesta-modal__body">

                <div className="citas-form-section">
                  <h6 className="citas-form-section__titulo"><i className="fas fa-calendar-check"></i> Resumen</h6>
                  <div className="row citas-form-row">

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Paciente:</label>
                        <input type="text" className="form-control" value={this.state.formWhatsappLlamada.nombre} readOnly/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Hora de la cita:</label>
                        <input type="text" className="form-control" value={this.state.formWhatsappLlamada.hora} readOnly/>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label>Institución:</label>
                        <input type="text" className="form-control" value={this.state.formWhatsappLlamada.institucion} readOnly/>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="citas-form-section">
                  <h6 className="citas-form-section__titulo"><i className="fas fa-mobile-alt"></i> Número de celular del paciente o persona responsable</h6>
                  <div className="row citas-form-row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <div className="input-group">
                          <div className="input-group-prepend">
                            <span className="input-group-text">+57</span>
                          </div>
                          <input type="text" maxLength="10" className="form-control camponum" placeholder="Ej: 3001234567" name="celular" autoComplete="off" value={this.state.formWhatsappLlamada.celular} onChange={this.handleChangeWhatsappLlamada}/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
              <div className="modal-footer justify-content-between citas-form-footer">
                <button type="button" className="btn btn-danger" data-dismiss="modal">Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={!this.state.active_button_whatsapp_llamada} onClick={()=> this.EnviarWhatsappLlamada()}> {this.state.loading_button_whatsapp_llamada?false:<i className="fas fa-circle-notch fa-spin"></i>} Enviar</button>
              </div>
            </div>
          </div>
        </div>

        <div className="modal fade" id="modal-info-paciente" data-backdrop="static">
          <div className="modal-dialog modal-lg">
            <div className="modal-content citas-respuesta-modal">
              <div className="modal-header citas-respuesta-modal__header">
                <h4 className="modal-title citas-respuesta-modal__title"><i className="fas fa-id-card"></i> Información del paciente</h4>
                <button type="button" className="close citas-respuesta-modal__close" data-dismiss="modal" aria-label="Close">
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body citas-respuesta-modal__body">
                {this.state.pacienteInfo ? (
                  <React.Fragment>

                    <div className="videollamada-info__avatar-header">
                      <div className="videollamada-info__avatar">
                        {this.state.avatarPaciente && !this.state.avatarError ? (
                          <img src={this.urlAvatarPaciente()} alt={this.state.nombrePaciente} onError={() => this.setState({ avatarError:true })}/>
                        ) : (
                          <i className={"fas " + this.iconoAvatarPorDefecto()}></i>
                        )}
                      </div>
                      <div className="videollamada-info__avatar-nombre">{this.nombreCompletoPaciente() || 'N/A'}</div>
                    </div>

                    <div className="citas-form-section">
                      <h6 className="citas-form-section__titulo"><i className="fas fa-user"></i> Identificación</h6>
                      <div className="videollamada-info__grid">
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Tipo ID</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.typeId || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Identificación</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.identification || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item videollamada-info__item--ancho">
                          <span className="videollamada-info__label">Nombre</span>
                          <span className="videollamada-info__valor">{this.nombreCompletoPaciente() || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Sexo</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.gender || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Estado civil</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.civilStatus || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Fecha de nacimiento</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.birthday ? moment(this.state.pacienteInfo.birthday).format('DD/MM/YYYY') : 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Edad</span>
                          <span className="videollamada-info__valor">{calcularEdadTexto(this.state.pacienteInfo.birthday) || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="citas-form-section">
                      <h6 className="citas-form-section__titulo"><i className="fas fa-address-book"></i> Contacto y ubicación</h6>
                      <div className="videollamada-info__grid">
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Teléfono</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.phone || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Correo</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.email || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item videollamada-info__item--ancho">
                          <span className="videollamada-info__label">Dirección</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.address || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Ciudad</span>
                          <span className="videollamada-info__valor">{(this.state.pacienteInfo.municipality && this.state.pacienteInfo.municipality.nombre) || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Departamento</span>
                          <span className="videollamada-info__valor">{(this.state.pacienteInfo.department && this.state.pacienteInfo.department.nombre) || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Ocupación</span>
                          <span className="videollamada-info__valor">{(this.state.pacienteInfo.activity && this.state.pacienteInfo.activity.name) || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="citas-form-section">
                      <h6 className="citas-form-section__titulo"><i className="fas fa-user-friends"></i> Persona responsable</h6>
                      <div className="videollamada-info__grid">
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Persona responsable</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.personInCharge || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Parentesco</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.relationshipPerson || 'N/A'}</span>
                        </div>
                        <div className="videollamada-info__item">
                          <span className="videollamada-info__label">Teléfono</span>
                          <span className="videollamada-info__valor">{this.state.pacienteInfo.phonePerson || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                  </React.Fragment>
                ) : (
                  <p className="videollamada-info__vacio">No hay información disponible del paciente.</p>
                )}
              </div>
              <div className="modal-footer justify-content-end citas-form-footer">
                <button type="button" className="btn btn-secondary" data-dismiss="modal">Cerrar</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }

}

export default VideoLlamadaZego;
